import router from '../router';
import Swal from 'sweetalert2';
import { fetchAPI } from '../modules/fetchAPI';

// ===== Cookie helpers =====
function setCookie(name, value, { seconds, domain, path = '/' } = {}) {
    let cookie = encodeURIComponent(name) + '=' + encodeURIComponent(value) + '; Path=' + path;
    if (domain) cookie += '; Domain=' + domain;
    if (seconds != null) {
        const d = new Date(Date.now() + seconds * 1000);
        cookie += '; Expires=' + d.toUTCString();
    }
    cookie += '; SameSite=Lax; Secure'; 
    document.cookie = cookie;
}
function getCookie(name) {
    const key = encodeURIComponent(name) + '='; 
    for (const part of document.cookie.split(';')) {
        const c = part.trim();
        if (c.startsWith(key)) return decodeURIComponent(c.slice(key.length));
    }
    return null;
}
// ==========================


function getAuthExpirationTimeFromCookie() {

    const regex = /(^|;)\s*eValAuthExpirationTime\s*=\s*([^;]+)/;
    const match = regex.exec(document.cookie);

    if (!match) return null;
    
    try {
        const val = decodeURIComponent(match.pop());
        const dt = new Date(val);
        if (Number.isNaN(dt.getTime())) return null;
        return dt;
    } catch {
        return null;
    }
}
export function useSessionTimeout(options) {
    const eValTimeoutCookie = "eValTimeoutSync";
    const settings = {
        title: 'Are you still there?',
        message: 'Your session is about to expire in {0} seconds.',
        question: 'Would you like to continue working?',
        keepAliveButtonText: 'Yes continue',
        signOutButtonText: 'No just logout',
        keepAliveUrl: options.keepAliveUrl.value,   // MVC endpoint (GET) that refreshes auth cookie       
        notify_offset_seconds: options.notificationCountdownInSeconds.value,   // show dialog this many seconds before expiry
        safety_buffer_seconds: 30,   // subtract from cookie expiry as a safety margin
        retry_interval_seconds: 3,    // retry delay when cookie is absent       
        logouturl: options.eValuatorUri.value + "/Account/LogOff"
    };

  
    // Flicker & de-dup guards  
    let logoutInProgress = false;
   
    let pollTimeoutId = null; // smart setTimeout — recalculated after each check
    let countdownIntervalId = null; // 1-second ticker to refresh the countdown display
    let lastKnownExpiry = null; // ISO string — detect when server extends the session
    let cookieChangeHandler = null; // CookieStore listener ref — kept for removeEventListener
    let changeDetectIntervalId = null; // setInterval for cookie-change detection (non-CookieStore only)

     
  
    // ── Countdown ticker (1-second refresh while dialog is open) ─────
    function startCountdownTicker() {
        if (countdownIntervalId) return; // already running
        countdownIntervalId = setInterval(() => {
            const expiryMs = readExpiryMs();
            if (expiryMs === null) return;
            const remaining = Math.max(0, Math.floor((expiryMs - Date.now()) / 1000));
            let timeoutcountdown = document.getElementById('timeout-countdown');
            if (dialogOpen && timeoutcountdown) {
                timeoutcountdown.textContent = remaining;
            }
          
        }, 1000);
    }

    function stopCountdownTicker() {
        if (countdownIntervalId) { clearInterval(countdownIntervalId); countdownIntervalId = null; }
    }

    function closeModal() {
        stopCountdownTicker();      
        Swal.close();
    }

    // module-scoped guard so we don't open the dialog twice
    let dialogOpen = false;

    async function showOrUpdateDialog(countdown) {
        if (logoutInProgress || dialogOpen) return;
        dialogOpen = true;

        try {
            const result = await Swal.fire({
                html: `
        <div id="timeout-dialog">
          <label class="title" style="text:bold">${settings.title}</label>
          <p id="timeout-question">${settings.question}</p>
          <p id="timeout-message">
            ${settings.message.replace('{0}', `<span id="timeout-countdown"" style="font-weight:700;">${countdown}</span>`)}
          </p>
        </div>
      `,
                showCancelButton: true,
                confirmButtonText: settings.keepAliveButtonText,
                cancelButtonText: settings.signOutButtonText,
                reverseButtons: true,
                allowOutsideClick: false,
                cancelButtonColor: '#00b5e2',
                confirmButtonColor: '#440099',
                width: settings.dialogWidth,
                didOpen: () => {
                    startCountdownTicker();
                }
            });
            
            if (result.isConfirmed) {
                await keepAlive();   // do not re-open the same dialog inside keepAlive
                return;              // EARLY RETURN prevents any fall-through
            }

            // Dismiss includes: cancel, close, backdrop, esc, timer, etc.
           
            if (result.dismiss === "cancel") {
                await signOut('1','Manual Logoff'); //message id 1 is passed to show appropriate message in login page
                return;
            }
          
        } catch (err) {
            console.error('displayDialog error:', err);
            // Safe fallback—choose what your product wants here:
         
        } finally {
            dialogOpen = false; // release the guard
        }
       
    }

    async function keepAlive() {
        try {
           
            await fetchAPI('api/account/keepMeAlive', "", "GET")
                .then(response => {
                    if (!response.ok) {                      
                        router.push({ path: '/logout' })
                    }
                    return response.json();
                })
                .then(json => {
                    if (json.data === false) {                        
                         router.push({ path: '/logout' })
                    }
                    else { //success
                        // Server has written a fresh eValTimeoutSync cookie in the response.
                        // Update lastKnownExpiry and reschedule the timer for the new expiry.
                        lastKnownExpiry = getCookie(eValTimeoutCookie);
                        closeModal();
                        scheduleNextCheck();
                       
                    }
                })          
        }
        catch
        {
            alert('Unable to extend session due to network error. Please sign in again.');
        }
    }    
   

    async function signOut(messageId, reason) {
        if (logoutInProgress) return;
        logoutInProgress = true;

        stopPolling();
        closeModal();       
        router.push({
            path: '/logout',
            query: {
                reason: reason,
                messageId: messageId
            }
        });
        
    }
    
    // ── Smart scheduler ──────────────────────────────────────────────
    // Cancels any pending timeout, then schedules the next checkSession() call
    // at exactly the right moment based on the current cookie value.
    function scheduleNextCheck() {
        if (pollTimeoutId) { clearTimeout(pollTimeoutId); pollTimeoutId = null; }
        if (logoutInProgress) return;

        const expiryMs = readExpiryMs();
        if (expiryMs === null) {
            // Cookie absent — retry after a short delay.
            pollTimeoutId = setTimeout(checkSession, settings.retry_interval_seconds * 1000);
            return;
        }

        const remainingMs = expiryMs - Date.now();
        const notifyMs = settings.notify_offset_seconds * 1000;

        let delayMs;
        if (remainingMs > notifyMs) {
            // Sleep until the dialog should appear.
            delayMs = remainingMs - notifyMs;
        } else if (remainingMs > 0) {
            // Already inside the warning window — wake at the expiry moment.
            delayMs = remainingMs;
        } else {
            // Already expired — fire on the next event-loop tick.
            delayMs = 0;
        }

        pollTimeoutId = setTimeout(checkSession, delayMs);
    }

    // Returns the effective expiry as epoch ms (safety buffer already subtracted).
    function readExpiryMs() {
        const raw = getCookie(eValTimeoutCookie);
        if (!raw) return null;
        const t = new Date(raw).getTime();
        return Number.isFinite(t) ? t - (settings.safety_buffer_seconds * 1000) : null;
    }
    // ── Main check tick ──────────────────────────────────────────────
    function checkSession() {
        if (logoutInProgress) return;

        const rawCookie = getCookie(eValTimeoutCookie);

        // Cookie value changed — either the session was extended or it was deleted (logout).
        if (rawCookie !== lastKnownExpiry) {
            lastKnownExpiry = rawCookie;
            closeModal();

            const newExpiryMs = readExpiryMs();
            if (newExpiryMs === null || newExpiryMs <= Date.now()) {
                // Cookie was deleted (another tab logged out) or has a past-date value.
                // Navigate directly to the landing page — no need to re-post a logout form.
                logoutInProgress = true;
                stopPolling();
                window.location.reload();
                return;
            }

            scheduleNextCheck(); // recalculate timer for the new (extended) expiry
            return;
        }

        const expiryMs = readExpiryMs();
        if (expiryMs === null) {
            scheduleNextCheck(); // no cookie yet — retry
            return;
        }

        const remainingSec = Math.floor((expiryMs - Date.now()) / 1000);

        if (remainingSec <= 0) {
            signOut(1, 'User Inactivity. Automatic Logoff');
            return; // stopPolling() called inside signOut — no reschedule
        }

        if (remainingSec <= settings.notify_offset_seconds) {
            // Inside the warning window — show or update the countdown dialog.
            showOrUpdateDialog(remainingSec);
        } else {
            // Still healthy — close any stale dialog (e.g. user extended in another tab).
            closeModal();
        }

        scheduleNextCheck();
    }
    // === Poller to react to cookie-based signals (cross-subdomain) ===
    function startPolling() {
        lastKnownExpiry = getCookie(eValTimeoutCookie); // baseline — don't fire immediately

        if (window.cookieStore) {
            // CookieStore API (Chrome 87+, Edge 87+):
            // CookieChangeEvent fires in every tab that can read this cookie —
            // same-origin tabs AND cross-subdomain tabs sharing the same Domain —
            // giving instant cross-tab/app sync when the server writes a new expiry.
            cookieChangeHandler = (event) => {
                const affected = [...(event.changed || []), ...(event.deleted || [])];
                if (affected.some(c => c.name === eValTimeoutCookie)) {
                    checkSession(); // detects the new value, resets the timer
                }
            };
            window.cookieStore.addEventListener('change', cookieChangeHandler);
        } else {
            // No CookieChangeEvent — poll every retry_interval_seconds to detect
            // cross-tab cookie changes (logout or session extension from another tab)
            // that the smart timeout would otherwise miss.
            // The smart setTimeout from scheduleNextCheck() still handles precise expiry timing.
            changeDetectIntervalId = setInterval(() => {
                if (getCookie(eValTimeoutCookie) !== lastKnownExpiry) checkSession();
            }, settings.retry_interval_seconds * 1000);
        }

        // Always schedule the smart timeout — handles expiry detection in both branches.
        scheduleNextCheck();
    }  

    function stopPolling() {
        if (pollTimeoutId) { clearTimeout(pollTimeoutId); pollTimeoutId = null; }
        if (changeDetectIntervalId) { clearInterval(changeDetectIntervalId); changeDetectIntervalId = null; }
        stopCountdownTicker();
        if (window.cookieStore && cookieChangeHandler) {
            window.cookieStore.removeEventListener('change', cookieChangeHandler);
            cookieChangeHandler = null;
        }
    }

   
    //Boot
    startPolling();

    // Clean up timers when the page unloads so they don't keep
    // closures alive in the V8 heap across navigations.
    window.addEventListener('beforeunload', () => {
        stopPolling();    // clears pollTimeoutId + countdownIntervalId
    }, { once: true });
    

    return { startPolling,keepAlive, signOut };
}
